import * as React from 'react';
import { useNavigate  } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import CircularIndeterminate from '../components/CircularLoad';
import AddressForm from './AddressForm';
import PaymentForm from './PaymentForm';
import Review from './Review';
import SimpleAlert from '../components/SimpleAlert';
import ReactGA from 'react-ga4';
import { responseArray, authHeaders } from '../utils/apiFetch';

function Copyright() {
  return (
    <Typography variant="body2" color="text.secondary" align="center">
      {'Copyright © '}
      <Link color="inherit">
        Pharmacy
      </Link>{' '}
      {new Date().getFullYear()}
      {'.'}
    </Typography>
  );
}

const steps = ['Shipping address', 'Payment details', 'Review your order'];

export default function CheckoutPage() {
  const [formData, setFormData] = React.useState({});
  const [activeStep, setActiveStep] = React.useState(0);
  const [outOfStockMsg, setOutOfStockMsg] = React.useState('');
  const [checkoutLoading, setCheckoutLoading] = React.useState(true);
  const [checkoutError, setCheckoutError] = React.useState('');
  // Review page
  const [cartData, setCartData] = React.useState();

  const navigate = useNavigate();

  React.useEffect(() => {
    Promise.all([fetchFavAddress(), fetchCardInfo(), fetchCartProducts()]) // race condition; wait for all async to be resolved to initialize formData
      .then(([favAddressData, cardInfoData]) => {
        initializeFormData(favAddressData, cardInfoData);
      })
      .catch(error => {
        console.error('Error fetching data:', error);
        setCheckoutError('Unable to load checkout details.');
      })
      .finally(() => {
        setCheckoutLoading(false);
      });
  }, []);

  const handleFormChange = (newFormData) => {
    setFormData(newFormData);
  };

  const handleNext = () => {
    setActiveStep(activeStep + 1);
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  function getStepContent(step) {
    switch (step) {
      case 0:
        return <AddressForm formData={formData} updateFormData={handleFormChange}/>;
      case 1:
        return <PaymentForm formData={formData} updateFormData={handleFormChange}/>;
      case 2:
        return cartData && <Review cartData={cartData} formData={formData}/>;
      default:
        throw new Error('Unknown step');
    }
  }
  
  const fetchFavAddress = async () => {
    try {
      const response = await fetch('/api/user/addresses/form', {
        headers: authHeaders({
          Accept: 'application/json'
        })
      });
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const results = await response.json();
        if (response.ok) {
          return results;
        }
        return fetchSavedAddressesFallback(results.message);
      }
      return fetchSavedAddressesFallback('Unable to load your shipping address.');
    } catch (error) {
      console.error('Error:', error);
      return fetchSavedAddressesFallback('Unable to load your shipping address.');
    }
  };

  const fetchSavedAddressesFallback = async (fallbackMessage) => {
    try {
      const response = await fetch('/api/user/addresses', {
        headers: authHeaders({
          Accept: 'application/json'
        })
      });
      if (response.ok) {
        const results = await response.json();
        const fav_address = results.addresses?.[0];
        if (fav_address) {
          return {
            user: {
              phone_nb: results.phoneNb
            },
            fav_address
          };
        }
      }
    } catch (error) {
      console.error('Error:', error);
    }
    setCheckoutError(fallbackMessage || 'Unable to load your shipping address.');
    return {};
  };

  const fetchCardInfo = async () => {
    try {
      const response = await fetch('/api/user/card', {
        headers: authHeaders({
          Accept: 'application/json'
        })
      });
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const results = await response.json();
        return response.ok ? results : {};
      }
      return {};
    } catch (error) {
      console.error('Error:', error);
      return {};
    }
  };

  const fetchCartProducts = async () => {
    try {
      const response = await fetch('/api/user/cart', {
        headers: authHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        const cart = responseArray(data, 'cart');
        const nextCartData = { ...data, cart };
        setCartData(nextCartData);
        ReactGA.event('begin_checkout', {
          currency: 'USD',
          value: Number(nextCartData.totalPrice || 0),
          items: cart.filter(item => item?.product).map(item => ({
            item_id: item.product._id,
            item_name: item.product.english_name,
            affiliation: 'Online Store',
            price: item.product.price,
            quantity: item.quantity,
            item_category: item.product.category
          }))
        });
      } else {
        console.error('Failed to fetch products');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const initializeFormData = (favAddressData, cardInfoData) => {
    const safeAddressData = favAddressData || {};
    const safeCardData = cardInfoData || {};
    setFormData({
      first_name: safeAddressData.user?.first_name || '',
      last_name: safeAddressData.user?.last_name || '',
      street: safeAddressData.fav_address?.street || '',
      city: safeAddressData.fav_address?.city || '',
      state: safeAddressData.fav_address?.state || '',
      building: safeAddressData.fav_address?.building || '',
      floor: safeAddressData.fav_address?.floor || '',
      phone_nb: safeAddressData.user?.phone_nb || '',
      extra_info: safeAddressData.fav_address?.extra_info || '',
      decryptedCardName: safeCardData.decryptedCardName || '',
      decryptedCardNumber: safeCardData.decryptedCardNumber || '',
      decryptedCVV: safeCardData.decryptedCVV || '',
      decryptedExpDate: safeCardData.decryptedExpDate || '',
      saveAddress: 'no'
    });
  };

  const handlePlaceOrder = async() => {
    try {
      var data;
      const saveAddress = formData.saveAddress;
      if (saveAddress === 'yes') {
        data = 
        { 
          address: {
            street: formData.street,
            city: formData.city,
            state: formData.state,
            building: formData.building,
            floor: formData.floor,
            phone_nb: formData.phone_nb,
            extra_info: formData.extra_info
          }
        }
      }
      const submitCheckout = async () => fetch("/api/user/checkout", {
          method: "POST",
          headers: authHeaders({
            "Content-Type": "application/json",
            Accept: "application/json"
          }),
          body: JSON.stringify({saveAddress, data})
      });

      let response = await submitCheckout();
      let result = await readJsonResponse(response);

      if (response.ok) {
          const items = responseArray(cartData, 'cart').filter(item => item?.product).map(item => ({
            item_id: item.product._id,
            item_name: item.product.english_name,
            affiliation: 'Online Store',
            price: item.product.price,
            quantity: item.quantity
          }));
    
          // Send the purchase event to Google Analytics
          ReactGA.event('purchase', {
            transaction_id: 'T_123d45', // Assuming the API response includes an order ID
            affiliation: 'Online Store',
            value: cartData.totalPrice,
            currency: 'USD',
            items: items
          });

          sessionStorage.setItem('sessionMessage', 'Order placed');
          navigate("/user/order-history");
          window.location.reload();
      } else {
          setOutOfStockMsg(result.message);
      }
    } catch (error) {
        console.error("Error:", error);
        setOutOfStockMsg('Unable to place order. Please try again.');
    }
  }

  const readJsonResponse = async (response) => {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      return response.json();
    }
    return { success: false, message: await response.text() };
  };

  return (
    <React.Fragment>
      <CssBaseline />
      <Container component="main" maxWidth="sm" sx={{ mb: 4 }}>
        <Paper variant="outlined" sx={{ my: { xs: 3, md: 6 }, p: { xs: 2, md: 3 }, borderRadius: '20px' }}>
          <SimpleAlert key="message-alert" message={outOfStockMsg} type={'error'} display={outOfStockMsg !== '' ? 'block' : 'none'}/>
          <SimpleAlert key="checkout-error-alert" message={checkoutError} type={'error'} display={checkoutError !== '' ? 'block' : 'none'}/>
          <Typography component="h1" variant="h4" align="center" style={{color: 'green'}}>
            Checkout
          </Typography>
          <Stepper activeStep={activeStep} sx={{ pt: 3, pb: 5 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          {checkoutLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
              <CircularIndeterminate display={true}/>
            </Box>
          ) : activeStep === steps.length ? (
            <React.Fragment>
              <Typography variant="h5" gutterBottom>
                Thank you for your order.
              </Typography>
              <Typography variant="subtitle1">
                Your order number is #2001539. We have emailed your order
                confirmation, and will send you an update when your order has
                shipped.
              </Typography>
            </React.Fragment>
          ) : (
            <React.Fragment>
              {getStepContent(activeStep)}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                {activeStep !== 0 && (
                  <Button onClick={handleBack} variant="contained" sx={{ mt: 3, ml: 1, backgroundColor: 'green' }}>
                    Back
                  </Button>
                )}

                <Button
                  variant="contained"
                  onClick={activeStep === steps.length - 1 ? handlePlaceOrder : handleNext}
                  sx={{ mt: 3, ml: 1, backgroundColor: 'green' }}
                >
                  {activeStep === steps.length - 1 ? 'Place order' : 'Next'}
                </Button>
              </Box>
            </React.Fragment>
          )}
        </Paper>
        <Copyright />
      </Container>
    </React.Fragment>
  );
}
